
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useSupabaseUser } from "@/hooks/use-supabase-user";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ClipboardList } from "lucide-react";

interface NewMission {
  id: string;
  title: string;
  team_id: string;
  created_at: string;
}

export const MissionsMenu = () => {
  const navigate = useNavigate();
  const { user } = useSupabaseUser();
  const { toast } = useToast();
  const [newMissions, setNewMissions] = useState<NewMission[]>([]);
  const [missionCount, setMissionCount] = useState(0);
  const channelRef = useRef<any>(null);

  useEffect(() => {
    const fetchNewMissions = async () => {
      try {
        if (!user) return;

        const { data: teamMembers } = await supabase
          .from('team_members')
          .select('team_id')
          .eq('profile_id', user.id);

        if (!teamMembers?.length) return;

        const teamIds = teamMembers.map(tm => tm.team_id);

        const timestamp = new Date();
        timestamp.setHours(timestamp.getHours() - 24);

        const { data: missions, error } = await supabase
          .from('missions')
          .select('id, title, team_id, created_at')
          .in('team_id', teamIds)
          .gt('created_at', timestamp.toISOString())
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error fetching missions:', error);
          return;
        }

        if (missions) {
          setNewMissions(missions);
          setMissionCount(missions.length);
        }

        // Clean up existing channel before creating a new one
        if (channelRef.current) {
          supabase.removeChannel(channelRef.current);
          channelRef.current = null;
        }

        // Create new channel and store reference
        channelRef.current = supabase.channel(`mission-notifications-${user.id}-${Date.now()}`)
          .on('postgres_changes', {
            event: 'INSERT',
            schema: 'public',
            table: 'missions',
            filter: `team_id=in.(${teamIds.map(id => `"${id}"`).join(',')})`,
          }, (payload) => {
            const newMission = payload.new as NewMission;
            setNewMissions(prev => [newMission, ...prev]);
            setMissionCount(prev => prev + 1);

            toast({
              title: "New Mission Created",
              description: `A new mission "${newMission.title}" has been created`,
            });
          })
          .subscribe();

      } catch (error) {
        console.error('Error fetching missions:', error);
      }
    };

    if (user) {
      fetchNewMissions();
    }

    // Cleanup function
    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [toast, user]);

  const handleMissionClick = (teamId: string, missionId: string) => {
    setNewMissions(prev => prev.filter(mission => mission.id !== missionId));
    setMissionCount(prev => Math.max(0, prev - 1));
    navigate(`/missions/team/${teamId}`);
  };

  return (
    <DropdownMenu>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="text-gray-600 relative dark:text-gray-300">
                <ClipboardList className="h-5 w-5" />
                {missionCount > 0 && (
                  <Badge 
                    variant="destructive" 
                    className="absolute top-0 right-5 w-3 h-3 flex items-center justify-center p-0 text-[8px] rounded-full bg-red-600 text-white"
                  >
                    {missionCount}
                  </Badge>
                )}
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent>
            <p>Work Instructions{missionCount > 0 ? ` (${missionCount})` : ""}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <DropdownMenuContent align="end" className="w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg z-50">
        {missionCount > 0 ? (
          <>
            <div className="px-2 py-1.5 text-sm font-medium text-gray-500">
              New Work Instructions
            </div>
            {newMissions.map((mission) => (
              <DropdownMenuItem 
                key={mission.id}
                onClick={() => handleMissionClick(mission.team_id, mission.id)}
                className="flex flex-col items-start p-2"
              >
                <div className="font-medium">{mission.title}</div>
                <div className="text-sm text-gray-500">
                  Created: {format(new Date(mission.created_at), 'dd/MM/yyyy HH:mm')}
                </div>
              </DropdownMenuItem>
            ))}
          </>
        ) : (
          <DropdownMenuItem disabled>No new work instructions</DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
